import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import { Activity, useMemo, useState } from 'react';
import SSOAttributeRuleModal from './sso-attribute-rule-modal';
import DeleteRuleModal from './delete-rule-modal';
import useComputedRegistrationSettings from '../../../hooks/use-computed-registration-settings';
import useLoadSSOs from '../../../hooks/use-load-ssos';
import { SSOAttributeRule, SSOAttributeRuleType, SSOOptionsType } from '@/types/registration.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Switch from '@ui/switch/switch';
import Chip from '@styles/design-system/components/chips/chip';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import Popover from '@ui/popover/popover';
import { plural } from '@/utils/plural';
import { getUser } from '@/store/slices/user/selectors';
import TextAlert from '@ui/text-alert/text-alert';
import TextAlertTitle from '@ui/text-alert/text-alert-title';
import { getDefaultSSOAttributeRule, isValidAttributeRule } from '@/utils/authentication/sso-utils';

export default function AccessControlSettings() {
  const dispatch = useAppDispatch();
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

  const user = useAppSelector(getUser);
  const [selectedRuleToEdit, setSelectedRuleToEdit] = useState<SSOAttributeRule>(getDefaultSSOAttributeRule(user?.id));
  const [selectedRuleToDelete, setSelectedRuleToDelete] = useState<SSOAttributeRule | null>(null);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const { registrationSettings } = useComputedRegistrationSettings();

  const { ssos } = useLoadSSOs();

  const selectedCustomSso = useMemo(() => {
    return ssos.find((sso: SSOOptionsType) => registrationSettings?.singleSignOn?.singleSignOns?.includes(sso.uuid));
  }, [registrationSettings?.singleSignOn?.singleSignOns, ssos]);

  const rules = useMemo(() => {
    return registrationSettings?.singleSignOn?.rules?.toSorted((a, b) => b.createdAt.localeCompare(a.createdAt)) ?? [];
  }, [registrationSettings?.singleSignOn?.rules]);

  const handleAddRule = () => {
    setSelectedRuleToEdit(getDefaultSSOAttributeRule(user?.id));
    setShowAddRuleModal(true);
  };

  const handleToggleRule = (uuid: string, on: boolean) => {
    if (!registrationSettings?.singleSignOn) return;
    const updatedRules = rules.map((rule) =>
      rule.uuid === uuid ? { ...rule, enabled: on, updated_at: new Date().toISOString(), updated_by: user?.id } : rule
    );
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, rules: updatedRules },
        },
      })
    );
  };

  const handleConfirmDeleteRule = () => {
    if (!registrationSettings?.singleSignOn || !selectedRuleToDelete) return;
    const updatedRules = rules.filter((rule) => rule.uuid !== selectedRuleToDelete.uuid);
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, rules: updatedRules },
        },
      })
    );
    setSelectedRuleToDelete(null);
  };

  const handleEditRule = (uuid: string) => {
    const rule = rules.find((rule) => rule.uuid === uuid);
    if (!rule) return;
    setSelectedRuleToEdit(rule);
    setShowAddRuleModal(true);
  };

  const handleEditRuleClose = () => {
    setSelectedRuleToEdit(getDefaultSSOAttributeRule(user?.id));
    setShowAddRuleModal(false);
  };

  const allRulesValid = useMemo(() => {
    return rules.every((rule) => selectedCustomSso && isValidAttributeRule(rule, selectedCustomSso));
  }, [rules, selectedCustomSso]);

  const renderRule = (rule: SSOAttributeRule) => {
    const isValid = selectedCustomSso && isValidAttributeRule(rule, selectedCustomSso);
    return (
      <Container
        design="GlobalCont2"
        borderRadius="12px"
        padding="16px"
        border={isValid ? 'none' : '1px solid var(--global-error-500)'}
        key={rule.uuid}
        className="flex ai-center jc-space-between"
      >
        <div className="flex gap-16 ai-center">
          <Text className="S2">{rule.name}</Text>
          <div className="flex gap-8 ai-center">
            <Chip label={rule.type === SSOAttributeRuleType.DENY ? 'Deny' : 'Allow'} size={32} type="info" />
            <Chip
              label={`${rule.conditions.length} ${plural(rule.conditions.length, 'condition')}`}
              size={32}
              type="info"
            />
          </div>
        </div>
        <div className="flex gap-8 ai-center">
          <Switch disabled={savingWorkingProject} value={rule.uuid} on={rule.enabled} onClick={handleToggleRule} />
          <Popover
            width="fit-content"
            renderTriggerButton={() => (
              <Button design="LocalBttnSecTransp" designSize="small" round>
                <Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={12} />
              </Button>
            )}
          >
            <Container
              background={'var(--gray-800)'}
              className="flex fd-column gap-8 padding-h-16 padding-v-8 width-100-percent"
              borderRadius="12px"
            >
              <Button clear onClick={() => handleEditRule(rule.uuid)} className="flex ai-center gap-8">
                <Icon name={ICONS.EDIT} size={16} />
                <Text whiteSpace="nowrap">Edit</Text>
              </Button>
              <Button clear onClick={() => setSelectedRuleToDelete(rule)} className="flex ai-center gap-8">
                <Icon name={ICONS.TRASH} size={16} />
                <Text whiteSpace="nowrap">Delete</Text>
              </Button>
            </Container>
          </Popover>
        </div>
      </Container>
    );
  };

  return (
    <OptionalComponent display={!!selectedCustomSso}>
      <Container
        design="GlobalCont1"
        borderRadius="12px"
        padding="16px"
        className="flex fd-column gap-8"
        border={allRulesValid ? 'none' : '1px solid var(--global-error-500)'}
      >
        <div className="flex ai-center jc-space-between">
          <Text tag="h6" className="H6">
            Access Control Settings
          </Text>
          <div>
            <Button
              design="LocalBttnSec"
              designSize="small"
              className="flex ai-center jc-center gap-6"
              onClick={handleAddRule}
            >
              <Icon name={ICONS.ADD} size={12} />
              <Text>Add rule</Text>
            </Button>
          </div>
        </div>
        <Activity mode={allRulesValid ? 'hidden' : 'visible'}>
          <TextAlert severity="error">
            <TextAlertTitle>Invalid rules</TextAlertTitle>
            <Text className="C2">Your SSO Configuration has changed, invalidating your existing ruleset</Text>
          </TextAlert>
        </Activity>
        <OptionalComponent display={rules.length > 0}>
          <hr style={{ borderColor: 'var(--gray-700)', width: '100%' }} />
          <Text className="S1">Attribute Rules</Text>
          <>{rules.map(renderRule)}</>
        </OptionalComponent>
      </Container>
      <SSOAttributeRuleModal
        open={showAddRuleModal}
        onClose={handleEditRuleClose}
        existingRule={selectedRuleToEdit}
        selectedCustomSSO={selectedCustomSso as SSOOptionsType}
      />
      <DeleteRuleModal
        open={!!selectedRuleToDelete}
        onClose={() => setSelectedRuleToDelete(null)}
        onDelete={handleConfirmDeleteRule}
      />
    </OptionalComponent>
  );
}
